/**
 * The TAX-GROUPS slice of the admin surface — design U5 (migration 30, OD-1).
 *
 * A FOURTH api file, for the reason `catalogue-api.ts` is a third: one owner per
 * file, and DTOs transcribed HERE rather than imported from `features/quotes/api.ts`
 * — that module's `TaxGroupDto` exists for the BUILDER's picker, and the catalogue
 * file records what sharing a hand-maintained interface across screens with
 * different jobs has already cost this app once. Each screen's own suite builds its
 * own fixture; a server change breaks the screen that cares, loudly, in that
 * screen's tests.
 *
 * Every call goes through `apiRequest` (single-flight refresh mutex, D-web-1).
 *
 * THE RATE IS READ HERE AND ONLY HERE on the web: the admin detail route is
 * `config.manage`-gated, and the country feed deliberately carries no rate values
 * (`tax_group.updated` says `rate_changed`, never the figure). A rate is a
 * PERCENTAGE — registered non-money — so it renders unmasked; it is still nobody's
 * input but the admin's.
 */

import { apiRequest } from '../../lib/api';
import type { CountryCode } from '../../domain/countries';

/** `TaxGroupDto` — transcribed from `countries/tax-groups.service.ts`. */
export interface AdminTaxGroupDto {
  id: string;
  country_id: number;
  name: string;
  /** `'NONE' | 'IN_GST_STATE_SPLIT'` — which resolve-time branching the group uses. */
  split_mode: string;
  /** At most one per country; the server's NULL-unique key is the floor. */
  is_default: boolean;
  active: boolean;
  created_at: string | null;
}

/** `TaxGroupLineDto` — one dated rate window inside a group. */
export interface AdminTaxGroupLineDto {
  id: string;
  tax_group_id: string;
  /** `'ALWAYS' | 'INTRA_STATE' | 'INTER_STATE'`. */
  applies: string;
  tax_name: string;
  /** DECIMAL(6,3) as a string — a percentage, never parsed into arithmetic here. */
  rate: string;
  effective_from: string;
  effective_to: string | null;
  sort_order: number;
}

export interface AdminTaxGroupDetailDto extends AdminTaxGroupDto {
  lines: AdminTaxGroupLineDto[];
}

export interface CreateTaxGroupBody {
  name: string;
  split_mode?: string;
  is_default?: boolean;
  /** MAY be empty — a staged group; lines are added in the editor afterwards. */
  lines: Array<{
    applies?: string;
    tax_name: string;
    rate: string;
    effective_from: string;
    effective_to?: string | null;
    sort_order?: number;
  }>;
}

/**
 * The ADMIN list: `includeInactive=1`, because retirement only removes a group
 * from the PICKER (M6) — the admin screen is exactly where the retired ones must
 * stay visible, with their state named.
 */
export async function fetchAdminTaxGroups(
  countryCode: CountryCode,
  signal?: AbortSignal,
): Promise<AdminTaxGroupDto[]> {
  const body = await apiRequest<{ data: AdminTaxGroupDto[] }>(
    `/countries/${countryCode}/tax-groups`,
    { query: { includeInactive: 1 }, signal },
  );
  return body.data;
}

export async function fetchTaxGroupDetail(
  countryCode: CountryCode,
  id: string,
  signal?: AbortSignal,
): Promise<AdminTaxGroupDetailDto> {
  return apiRequest<AdminTaxGroupDetailDto>(`/countries/${countryCode}/tax-groups/${id}`, {
    signal,
  });
}

export async function createTaxGroup(
  countryCode: CountryCode,
  body: CreateTaxGroupBody,
): Promise<AdminTaxGroupDetailDto> {
  return apiRequest<AdminTaxGroupDetailDto>(`/countries/${countryCode}/tax-groups`, {
    method: 'POST',
    body,
  });
}

export async function patchTaxGroup(
  countryCode: CountryCode,
  id: string,
  body: { name?: string; active?: boolean; is_default?: boolean },
): Promise<AdminTaxGroupDetailDto> {
  return apiRequest<AdminTaxGroupDetailDto>(`/countries/${countryCode}/tax-groups/${id}`, {
    method: 'PATCH',
    body,
  });
}

export async function addTaxGroupLine(
  countryCode: CountryCode,
  id: string,
  body: {
    applies?: string;
    tax_name: string;
    rate: string;
    effective_from: string;
    effective_to?: string | null;
    sort_order?: number;
  },
): Promise<AdminTaxGroupDetailDto> {
  return apiRequest<AdminTaxGroupDetailDto>(
    `/countries/${countryCode}/tax-groups/${id}/lines`,
    { method: 'POST', body },
  );
}

export async function patchTaxGroupLine(
  countryCode: CountryCode,
  id: string,
  lineId: string,
  body: {
    applies?: string;
    tax_name?: string;
    rate?: string;
    effective_from?: string;
    effective_to?: string | null;
    sort_order?: number;
  },
): Promise<AdminTaxGroupDetailDto> {
  return apiRequest<AdminTaxGroupDetailDto>(
    `/countries/${countryCode}/tax-groups/${id}/lines/${lineId}`,
    { method: 'PATCH', body },
  );
}

/** Only a group holding nothing deletes; anything else is a 409 naming the counts. */
export async function deleteTaxGroup(
  countryCode: CountryCode,
  id: string,
): Promise<{ deleted: true; id: string }> {
  return apiRequest<{ deleted: true; id: string }>(
    `/countries/${countryCode}/tax-groups/${id}`,
    { method: 'DELETE' },
  );
}

export const taxGroupAdminKeys = {
  list: (cc: CountryCode) => ['admin-tax-groups', cc] as const,
  detail: (cc: CountryCode, id: string) => ['admin-tax-groups', cc, id] as const,
};
